import { useState } from 'react';
import { Num, Plural, T } from 'gt-react';
import type { WorkbookTable as TableData } from '@erp/shared-types';
import type { ColumnType } from '@erp/workbooks';
import { WorkbookTable } from './WorkbookTable';
import type { CellFilterTarget } from './WorkbookCellMenu';

/**
 * A Source preview as the server returns it: the first rows and how many there
 * are, or about how many (`rowCountEstimated`) until the true count is read.
 */
export type SourcePreviewData = TableData & {
  rowCount: number;
  rowCountEstimated?: boolean;
};

/**
 * A Source's preview: how many of its rows it shows, then the rows. While a new
 * read is pending or after one fails, the last preview read stays in place (marked
 * as such), so a filter that fails, or a slow read, never blanks the table or
 * shifts the layout.
 */
export function WorkbookSourcePreview({
  data,
  error,
  pending,
  types,
  filterTarget,
  fill = false,
}: {
  data: SourcePreviewData | undefined;
  error: Error | null;
  pending: boolean;
  types?: ReadonlyMap<string, ColumnType>;
  filterTarget?: CellFilterTarget;
  /** Fill the height left in a flex column, rather than stopping at 55vh. */
  fill?: boolean;
}) {
  const [lastGood, setLastGood] = useState(data);
  // Adjusting state while rendering, as React recommends for state derived from props.
  if (data && data !== lastGood) setLastGood(data);
  const shown = data ?? lastGood;
  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col gap-2' : 'space-y-2'}>
      {/* One line, always present, so the table never moves as the count arrives. */}
      <p className="min-h-4 text-xs text-muted-foreground tabular-nums">
        {shown ? (
          shown.rowCountEstimated ? (
            <T context="workbook source preview count, of about how many rows">
              Showing <Num>{shown.rows.length}</Num> of ~
              <Num>{shown.rowCount}</Num> rows
            </T>
          ) : shown.rowCount > shown.rows.length ? (
            <T context="workbook source preview count">
              Showing <Num>{shown.rows.length}</Num> of{' '}
              <Num>{shown.rowCount}</Num> rows
            </T>
          ) : (
            <T context="workbook source preview count, every row">
              <Num>{shown.rowCount}</Num>{' '}
              <Plural n={shown.rowCount} one="row" other="rows" />
            </T>
          )
        ) : pending ? (
          <T context="workbook loading source">Loading source…</T>
        ) : null}
        {shown && !data && (pending || error) && (
          <>
            {' · '}
            {pending ? (
              <T context="workbook source preview refreshing">Updating…</T>
            ) : (
              <T context="workbook source preview is the last one read">
                Showing the last preview that could be read
              </T>
            )}
          </>
        )}
      </p>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error.message}
        </p>
      )}
      {shown && (
        <WorkbookTable
          table={shown}
          types={types}
          filterTarget={filterTarget}
          fill={fill}
        />
      )}
    </div>
  );
}
